<template>
  <div class="lm-access">
    <div class="cm-seg" role="tablist">
      <button type="button" :class="{ active: pane === 'workspace' }" :aria-selected="pane === 'workspace'" @click="pane = 'workspace'">
        {{ $t("LM_WORKSPACE_ACCESS") }}
        <span class="lm-count">{{ getGrantedAccesses.length }}</span>
      </button>
      <button type="button" :class="{ active: pane === 'category' }" :aria-selected="pane === 'category'" @click="pane = 'category'">
        {{ $t("LM_CATEGORY_ACCESS") }}
        <span class="lm-count">{{ flattenedCategoryAccess.length }}</span>
      </button>
    </div>

    <div v-show="pane === 'workspace'">
      <form @submit.prevent="giveAccess" class="lm-invite">
        <label class="lm-field email-field">
          <span>{{ $t("ACCESS_MANAGER_EMAIL") }}</span>
          <input type="email" class="inp-st" v-model="admin_email" :aria-invalid="!!emailError" :disabled="granting" />
        </label>
        <div class="lm-field">
          <span>{{ $t("ACCESS_MANAGER_ACCESS") }}</span>
          <div class="period-btns" v-click-outside="() => closeMenu('grant')">
            <span class="cm-select-label" :class="{ active: openMenu === 'grant' }" @click="toggleMenu('grant')">
              {{ access_type ? $t("ACCESS_MANAGER_FULL_ACCESS") : $t("ACCESS_MANAGER_READ_ONLY") }}
              <span class="expand"></span>
            </span>
            <ul v-show="openMenu === 'grant'">
              <li :class="{ active: access_type }" @click="pickAccess(true)">{{ $t("ACCESS_MANAGER_FULL_ACCESS") }}</li>
              <li :class="{ active: !access_type }" @click="pickAccess(false)">{{ $t("ACCESS_MANAGER_READ_ONLY") }}</li>
            </ul>
          </div>
        </div>
        <button class="btn-st-red" type="submit" :disabled="granting">
          {{ granting ? $t("LM_GRANTING") : $t("ACCESS_MANAGER_GRANT_ACCESS") }}
        </button>
        <p class="lm-error" v-if="emailError">{{ emailError }}</p>
      </form>
      <div class="lm-people-head">
        <span>{{ $t("LM_PEOPLE_WITH_ACCESS") }} - {{ getGrantedAccesses.length }}</span>
      </div>
      <table class="lm-table" v-if="getGrantedAccesses.length">
        <thead>
          <tr>
            <th>{{ $t("ACCESS_MANAGER_EMAIL") }}</th>
            <th>{{ $t("ACCESS_MANAGER_ACCESS") }}</th>
            <th>{{ $t("ACCESS_MANAGER_ACTION") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(admin, index) in getGrantedAccesses" :key="admin.email || index">
            <td>{{ admin.email }}</td>
            <td>
              <div class="period-btns" v-click-outside="() => closeMenu('role-' + index)">
                <span
                  class="cm-select-label"
                  :class="{ active: openMenu === 'role-' + index }"
                  @click="!isOwner(admin.email) && toggleMenu('role-' + index)"
                >
                  {{ admin.access_type ? $t("ACCESS_MANAGER_FULL_ACCESS") : $t("ACCESS_MANAGER_READ_ONLY") }}
                  <span class="expand"></span>
                </span>
                <ul v-show="openMenu === 'role-' + index && !isOwner(admin.email)">
                  <li :class="{ active: admin.access_type }" @click="setRole(admin, 1)">{{ $t("ACCESS_MANAGER_FULL_ACCESS") }}</li>
                  <li :class="{ active: !admin.access_type }" @click="setRole(admin, 0)">{{ $t("ACCESS_MANAGER_READ_ONLY") }}</li>
                </ul>
              </div>
            </td>
            <td>
              <button v-if="!isOwner(admin.email)" type="button" class="lm-remove" @click="askRemoveWorkspace(admin.email)">
                {{ $t("ACCESS_MANAGER_REMOVE_ACCESS") }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="lm-empty">{{ $t("ACCESS_MANAGER_NO_ACCESS") }}</p>
      <div ref="alertContainer"></div>
    </div>

    <div v-show="pane === 'category'">
      <form @submit.prevent="giveCategoryAccess" class="lm-invite category">
        <div class="lm-field">
          <div class="period-btns" v-click-outside="() => closeMenu('category')">
            <span class="cm-select-label" :class="{ active: openMenu === 'category' }" @click="toggleMenu('category')">
              {{ selectedCategoryName }}
              <span class="expand"></span>
            </span>
            <ul v-show="openMenu === 'category'">
              <li
                v-for="category in getCategories"
                :key="category.id"
                :class="{ active: String(categoryId) === String(category.id) }"
                @click="pickCategory(category.id)"
              >
                {{ category.name }}
              </li>
            </ul>
          </div>
        </div>
        <label class="lm-field email-field">
          <input type="email" class="inp-st" v-model="categoryEmail" :aria-invalid="!!categoryError" :disabled="grantingCategory" :placeholder="$t('ACCESS_MANAGER_EMAIL')" />
        </label>
        <button class="btn-st-red" type="submit" :disabled="grantingCategory">
          {{ grantingCategory ? $t("LM_GRANTING") : $t("ACCESS_MANAGER_GRANT_ACCESS") }}
        </button>
        <p class="lm-error" v-if="categoryError">{{ categoryError }}</p>
      </form>
      <table class="lm-table" v-if="flattenedCategoryAccess.length">
        <thead>
          <tr>
            <th>{{ $t("CATEGORY_ACCESS_CATEGORY") }}</th>
            <th>{{ $t("ACCESS_MANAGER_EMAIL") }}</th>
            <th>{{ $t("ACCESS_MANAGER_ACTION") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(accessItem, index) in flattenedCategoryAccess" :key="index">
            <td>
              <span :class="`cat-indicator ${getCategoryColorClass(accessItem.category.id)}`"></span>
              {{ accessItem.category.name }}
            </td>
            <td>{{ accessItem.email }}</td>
            <td>
              <button type="button" class="lm-remove" @click="askRemoveCategory(accessItem)">
                {{ $t("ACCESS_MANAGER_REMOVE_ACCESS") }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else-if="categoriesLoaded" class="lm-empty">{{ $t("CATEGORY_ACCESS_NO_SHARED") }}</p>
    </div>

    <ConfirmDialog
      :open="!!pendingRemove"
      :title="$t('LM_REMOVE_ACCESS_TITLE')"
      :description="pendingRemove ? $t('LM_REMOVE_ACCESS_BODY', { email: pendingRemove.email }) : ''"
      :confirm-label="$t('ACCESS_MANAGER_REMOVE_ACCESS')"
      :cancel-label="$t('LM_CANCEL')"
      @confirm="confirmRemove"
      @cancel="pendingRemove = null"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import { INTRO_SET_ACCESS_MANAGER_VISIBLE } from "../../store/actions/intro";
import {
  ADMIN_DELETE,
  ADMIN_SET,
  ADMIN_CHANGE_ACCESS,
} from "../../store/actions/admin";
import {
  CATEGORIES_SET_LIST,
  CATEGORIES_SET_ACCESS_LIST,
  CATEGORIES_REMOVE_ACCESS,
  CATEGORIES_SHARE_ACCESS,
} from "../../store/actions/categories";
import module from '@/models/AxiosModule'
import PageUtils from '@/models/page-utils'
import ConfirmDialog from './ConfirmDialog.vue'
import ClickOutside from 'vue-click-outside'

export default {
  name: "AccessManager",
  components: { ConfirmDialog },
  directives: { ClickOutside },
  computed: {
    ...mapGetters(["getGrantedAccesses", "getCategories", "getCategoriesAccessList", "getProfile"]),
    
    categoriesWithAccess() {
      return this.categoriesWithAccessData.filter(category => 
        category.access_list && category.access_list.length > 0
      );
    },
    
    selectedCategoryName() {
      const category = (this.getCategories || []).find((item) => String(item.id) === String(this.categoryId));
      return category ? category.name : this.$t("CATEGORY_ACCESS_CATEGORY");
    },
    flattenedCategoryAccess() {
      const flattened = [];
      this.categoriesWithAccess.forEach(category => {
        category.access_list.forEach(access => {
          flattened.push({
            category: category,
            email: access.granted_to_email
          });
        });
      });
      return flattened;
    }
  },

  data() {
    return {
      pane: "workspace",
      openMenu: null,
      access_type: false,
      admin_email: "",
      emailError: "",
      granting: false,
      categoryId: "",
      categoryEmail: "",
      categoryError: "",
      grantingCategory: false,
      pendingRemove: null,
      emptyAlertInstance: null,
      categoriesWithAccessData: [],
      categoriesLoaded: false,
    };
  },
  mounted() {
    this.fetchGrantedAccesses();
    this.fetchCategoriesWithAccess();
  },
  methods: {
    ...mapActions({
      deleteAdmin: ADMIN_DELETE,
      changeAccess: ADMIN_CHANGE_ACCESS,
      fetchGrantedAccesses: ADMIN_SET,
      loadCategories: CATEGORIES_SET_LIST,
      setCategoryAccessList: CATEGORIES_SET_ACCESS_LIST,
      removeCategoryAccess: CATEGORIES_REMOVE_ACCESS,
      shareCategoryAccess: CATEGORIES_SHARE_ACCESS,
    }),
    ...mapMutations([INTRO_SET_ACCESS_MANAGER_VISIBLE]),

    toggleMenu(key) {
      this.openMenu = this.openMenu === key ? null : key;
    },
    closeMenu(key) {
      if (this.openMenu === key) this.openMenu = null;
    },
    pickAccess(full) {
      this.access_type = full;
      this.openMenu = null;
    },
    pickCategory(id) {
      this.categoryId = id;
      this.openMenu = null;
    },
    setRole(admin, accessType) {
      this.openMenu = null;
      if ((admin.access_type ? 1 : 0) === accessType) return;
      this.changeAccess({ email: admin.email, access_type: accessType });
    },
    isOwner(email) {
      return (email || "").toLowerCase() === (this.getProfile?.email || "").toLowerCase();
    },
    validateEmail(value, duplicates) {
      const email = (value || "").trim();
      if (!email) return this.$t("LM_EMAIL_REQUIRED");
      if (!PageUtils.isValidEmail(email)) return this.$t("LM_EMAIL_INVALID");
      if (duplicates.some((item) => (item || "").toLowerCase() === email.toLowerCase())) {
        return this.$t("LM_EMAIL_DUPLICATE");
      }
      return "";
    },
    giveAccess() {
      this.emailError = this.validateEmail(
        this.admin_email,
        (this.getGrantedAccesses || []).map((admin) => admin.email)
      );
      if (this.emailError || this.granting) return;
      this.granting = true;
      const axios = module.Axios;
      axios
        .post("create-admin-access", {
          access_type: this.access_type,
          admin_email: this.admin_email.trim(),
        })
        .then(() => {
          this.admin_email = "";
          this.emailError = "";
          this.$store.dispatch(ADMIN_SET).then(() => {
            if (this.getGrantedAccesses.length > 0) {
              this.clearAlert();
            }
          });
        })
        .catch((error) => {
          if (error?.response?.data?.error_code) {
            this.emailError = this.$t(error.response.data.error_code);
            this.showInlineAlert(this.$t(error.response.data.error_code));
          } else if (error?.response?.data) {
            this.showInlineAlert(this.$t("OTHER_ERROR"));
          }
        })
        .finally(() => {
          this.granting = false;
        });
    },
    async giveCategoryAccess() {
      const email = (this.categoryEmail || "").trim();
      const duplicates = this.flattenedCategoryAccess
        .filter((item) => String(item.category.id) === String(this.categoryId))
        .map((item) => item.email);
      this.categoryError = !this.categoryId
        ? this.$t("LM_SELECT_CATEGORY")
        : this.validateEmail(email, duplicates);
      if (this.categoryError || this.grantingCategory) return;
      this.grantingCategory = true;
      try {
        await this.shareCategoryAccess({
          categoryId: this.categoryId,
          granted_to_email: email,
        });
        this.categoryEmail = "";
        this.categoryError = "";
        await this.fetchCategoriesWithAccess();
        this.showInlineAlert(this.$t("CATEGORY_MANAGER_ACCESS_GRANTED"));
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.categoryError = this.$t(error.response.data.error_code);
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t("CATEGORY_MANAGER_ACCESS_ERROR"));
        }
      } finally {
        this.grantingCategory = false;
      }
    },
    askRemoveWorkspace(email) {
      this.pendingRemove = { kind: "workspace", email };
    },
    askRemoveCategory(accessItem) {
      this.pendingRemove = {
        kind: "category",
        email: accessItem.email,
        categoryId: accessItem.category.id,
      };
    },
    async confirmRemove() {
      const pending = this.pendingRemove;
      this.pendingRemove = null;
      if (!pending) return;
      if (pending.kind === "workspace") {
        this.deleteAdmin(pending.email);
        return;
      }
      await this.removeCategoryAccessFromUser(pending.categoryId, pending.email);
    },
    clearAlert() {
      if (this.emptyAlertInstance) {
        this.emptyAlertInstance.close();
        this.emptyAlertInstance = null;
      }
    },
    showInlineAlert(message) {
      this.$alert.show({
        message: message,
        type: 'inline',
        color: '',
        component: this,
        target: this.$refs.alertContainer,
      });
    },

    async fetchCategoriesWithAccess() {
      try {
        await this.loadCategories();
        const categories = this.getCategories || [];
        const axios = module.Axios;
        const categoriesWithAccess = await Promise.all(
          categories.map(async (category) => {
            try {
              const response = await axios.post('get-category-access', {
                id: category.id
              });
              
              const accessList = response.data.access_list || [];
              
              return {
                ...category,
                access_list: accessList
              };
            } catch (error) {
              return {
                ...category,
                access_list: []
              };
            }
          })
        );
        
        this.categoriesWithAccessData = categoriesWithAccess;
        this.categoriesLoaded = true;
      } catch (error) {
        this.categoriesWithAccessData = [];
        this.categoriesLoaded = true;
      }
    },

    async removeCategoryAccessFromUser(categoryId, email) {
      try {
        await this.removeCategoryAccess({
          categoryId: categoryId,
          email: email
        });
        await this.fetchCategoriesWithAccess();
        
        this.showInlineAlert(this.$t('CATEGORY_ACCESS_REMOVED_SUCCESS'));
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t('CATEGORY_ACCESS_REMOVE_ERROR'));
        }
      }
    },

    getCategoryColorClass(categoryId) {
      if (!categoryId) return 'gray-back';
      const stored = localStorage.getItem('categoryColors');
      const colors = stored ? JSON.parse(stored) : {};
      const color = colors[categoryId] || 'gray';
      
      return color + '-back';
    },
  },
};
</script>
